import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { SellerVerification, SellerVerificationKey } from '@/lib/marketplace/seller-verification'

import { BadgeCheck, ShieldCheck } from 'lucide-react'

import { cn } from '@/lib/utils'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

const ICONS: Record<SellerVerificationKey, LucideIcon> = {
    telegram: BadgeCheck,
    phone: ShieldCheck,
    business: ShieldCheck,
    identity: ShieldCheck
}

/** One hue per check, used on the ICON only. Never red. */
const TONES: Record<SellerVerificationKey, string> = {
    telegram: 'text-verify-telegram',
    phone: 'text-verify-phone',
    business: 'text-verify-business',
    identity: 'text-verify-identity'
}

interface VerificationIconProps {
    verification: SellerVerification
    className?: string
}

/**
 * One icon per check, each with its own hue when the check was actually
 * performed and grey when it was not. Red is deliberately not used here,
 * it belongs to the brand and to marketplace actions, so a buyer can tell a
 * confirmed identity apart from a call to action at a glance. The state is
 * never carried by colour alone, every use pairs it with a label and a title.
 */
export const SellerVerificationIcon: FC<VerificationIconProps> = ({ verification, className }): ReactNode => {
    const Icon = ICONS[verification.key]
    const tone = cn('shrink-0', verification.granted ? TONES[verification.key] : 'text-verify-off', className)

    if (verification.key === 'telegram') return <TelegramGlyph className={tone} />

    return <Icon aria-hidden='true' className={tone} />
}

interface SellerVerificationBadgesProps {
    verifications: Array<SellerVerification>
    className?: string
}

/**
 * The horizontal check row that sits under the seller's handle. Unverified
 * checks stay on the row and read as "not verified" rather than disappearing,
 * so a buyer can see what was NOT confirmed as easily as what was.
 */
export const SellerVerificationBadges: FC<SellerVerificationBadgesProps> = ({
    verifications,
    className
}): ReactNode => {
    return (
        <ul
            className={cn(
                'grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-0 sm:gap-y-2',
                className
            )}
        >
            {verifications.map((item, index) => (
                <li
                    key={item.key}
                    title={item.granted ? item.description : `${item.shortLabel} not verified.`}
                    className={cn(
                        'flex items-center gap-2 rounded-mta-xs border border-ink-900/15 bg-paper px-2.5 py-2.5',
                        'sm:border-transparent sm:bg-transparent sm:px-0 sm:py-0'
                    )}
                >
                    {index > 0 && (
                        <span aria-hidden='true' className='mr-5 hidden h-4 w-px bg-ink-900/15 sm:block' />
                    )}

                    <SellerVerificationIcon verification={item} className='size-[1.15rem]' />

                    <span
                        className={cn(
                            'text-[0.8125rem] font-semibold leading-tight sm:whitespace-nowrap sm:text-[0.9375rem] sm:leading-none',
                            item.granted ? 'text-ink-900' : 'text-text-muted'
                        )}
                    >
                        {item.granted ? `${item.shortLabel} Verified` : `${item.shortLabel} Unverified`}
                    </span>
                </li>
            ))}
        </ul>
    )
}
